import {
    IAppointmentChecks,
    IClientBooking,
    ISessionAttendee,
    ISessionAttendeeConsentCheck,
} from "intake_shared/src/types";
import { SESSION_ATTENDEE_SUPPORTED_SERVICES } from "intake_shared/src/constants";
import { v4 as uuidv4 } from "uuid";

export function isCollateralConsentService(appointment: IClientBooking): boolean {
    return (
        isSessionAttendeeSupportedService(appointment.requested_service) ||
        Boolean(
            appointment.session_attendees?.some(
                (attendee) =>
                    attendee.active !== false && attendee.guardian_role === "other"
            )
        )
    );
}

export function isSessionAttendeeSupportedService(service?: string): boolean {
    return SESSION_ATTENDEE_SUPPORTED_SERVICES.includes(service ?? "");
}

export function getConsentRelevantSessionAttendees(
    attendees: ISessionAttendee[],
    service?: string
): ISessionAttendee[] {
    const supportsGeneralAttendees = isSessionAttendeeSupportedService(service);
    return attendees.filter(
        (attendee) =>
            attendee.active !== false &&
            attendee.guardian_role !== "primary" &&
            (supportsGeneralAttendees || attendee.guardian_role === "other")
    );
}

export function getAttendeeFullName(attendee: Partial<ISessionAttendee>): string {
    const fromParts = [attendee.first_name, attendee.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
    return (fromParts || "").trim();
}

export function getAttendeeDob(
    attendee: Partial<ISessionAttendee>
): string | null | undefined {
    return attendee.date_of_birth;
}

export function buildEmptySessionAttendee(
    defaults: Partial<ISessionAttendee> = {}
): ISessionAttendee {
    const now = new Date();
    const attendee: ISessionAttendee = {
        id: uuidv4(),
        first_name: "",
        last_name: "",
        email: "",
        date_of_birth: null,
        relationship: "",
        active: true,
        consent_check: {
            good_form_on_file: false,
            rechecked_at: null,
            rechecked_by: null,
        },
        created_at: now,
        updated_at: now,
        ...defaults,
    };
    return {
        ...attendee,
        consent_check: getUpdatedAttendeeConsentCheck(attendee),
    };
}

export function isMinorDob(dob?: string | null, asOf = new Date()): boolean {
    if (!dob) return false;
    const birthDate = new Date(`${dob}T00:00:00`);
    if (Number.isNaN(birthDate.getTime())) return false;

    let age = asOf.getFullYear() - birthDate.getFullYear();
    const monthDiff = asOf.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < birthDate.getDate())) {
        age -= 1;
    }
    return age < 18;
}

export function getSessionAttendeeMissingFields(
    attendee: Partial<ISessionAttendee>,
    service?: string
): string[] {
    const missingFields: string[] = [];
    const dob = getAttendeeDob(attendee);
    if (!attendee.first_name?.trim() || !attendee.last_name?.trim())
        missingFields.push("name");
    if (service === "Family Therapy" && attendee.guardian_role !== "other" && !dob) {
        missingFields.push("date_of_birth");
    }
    if (!attendee.email?.trim() && !isMinorDob(dob)) {
        missingFields.push("email");
    }
    return missingFields;
}

export function getUpdatedAttendeeConsentCheck(
    attendee: ISessionAttendee,
    consentCheck: Partial<ISessionAttendeeConsentCheck> = {}
): ISessionAttendeeConsentCheck {
    const existingConsentCheck = {
        ...(attendee.consent_check || {}),
    };
    return {
        ...existingConsentCheck,
        ...consentCheck,
        good_form_on_file:
            consentCheck.good_form_on_file ??
            attendee.consent_check?.good_form_on_file ??
            false,
    };
}

export function getCollateralConsentAppointmentCheck(
    appointmentCheck: IAppointmentChecks[string] | undefined,
    appointment: IClientBooking,
    attendees = appointment.session_attendees || [],
    recheckedAt = new Date()
): IAppointmentChecks[string] {
    const existingAppointmentCheck: IAppointmentChecks[string] =
        appointmentCheck ?? {
            date_added: recheckedAt,
            client_requested_call: false,
            consult_call_notes: "",
        };

    if (
        !isCollateralConsentService({
            ...appointment,
            session_attendees: attendees,
        })
    ) {
        return existingAppointmentCheck;
    }

    const service = appointment.requested_service;
    const activeAttendees = getConsentRelevantSessionAttendees(attendees, service);
    const missingAttendeeCount = activeAttendees.filter(
        (attendee) =>
            getSessionAttendeeMissingFields(attendee, service).length > 0 ||
            !attendee.consent_check?.good_form_on_file
    ).length;

    return {
        ...existingAppointmentCheck,
        collateral_consent_good_form_on_file:
            activeAttendees.length > 0 && missingAttendeeCount === 0,
        collateral_consent_rechecked: recheckedAt,
        collateral_consent_missing_attendee_count: missingAttendeeCount,
    };
}
